import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Clock, Phone, Car, CheckCircle, Navigation, ArrowRight, Star, ChevronDown, AlertTriangle, Wrench, Shield } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema, CityServiceAreaSchema, CityReviewsSchema, FAQSchema } from "@/components/SchemaOrg";
import { TestimonialsCarousel } from "@/components/site/TestimonialsCarousel";
import { CityUseCases } from "@/components/site/CityUseCases";
import { CityComparison } from "@/components/site/CityComparison";
import { CityDefectsComparison } from "@/components/site/CityDefectsComparison";
import { cities } from "@/data/citiesData";
import calendrierWhiteIcon from "@/assets/icons/calendrier-white.webp";
import facadeCentre from "@/assets/centre/facade-centre.webp";
import accueilReception from "@/assets/centre/accueil-reception.webp";
import bancControle from "@/assets/centre/banc-controle-centre.webp";
import facadeEte from "@/assets/centre/facade-centre-ete.webp";

const CityLandingPage = () => {
  const { citySlug } = useParams<{ citySlug: string }>();
  const city = cities.find(c => c.slug === citySlug);

  if (!city) return <Navigate to="/" replace />;

  const googleMapsDirections = `https://www.google.com/maps/dir/${encodeURIComponent(city.name + ", " + city.postalCode)}/133+Boulevard+Aristide+Briand,+93100+Montreuil`;

  const cityFAQ = [
    { question: `Où se trouve le centre de CT le plus proche de ${city.name} ?`, answer: `Le centre Autosur Montreuil-sous-Bois est situé au 133 Bd Aristide Briand, 93100 Montreuil-sous-Bois, à seulement ${city.driveTime} en voiture de ${city.name} (${city.distanceKm}).` },
    { question: `Comment venir depuis ${city.name} ?`, answer: `En voiture : ${city.driveTime} (${city.distanceKm}). En transports en commun : ${city.transitTime} via ${city.transitDetails}. Parking gratuit et sécurisé sur place.` },
    { question: "Quels sont les horaires du centre ?", answer: "Le centre est ouvert du lundi au vendredi de 8h30 à 18h30, et le samedi de 9h00 à 13h00. Réservation en ligne disponible 24h/24, 7j/7." },
    { question: "Combien coûte le contrôle technique ?", answer: "Voiture : 73€ en ligne (80€ standard). Moto : 70€. Utilitaire : 79€ en ligne (86€ standard). Véhicule de collection : 73€. Économisez 7€ en réservant en ligne." },
    { question: "Puis-je venir sans rendez-vous ?", answer: "Oui, nous acceptons les passages sans rendez-vous le samedi matin selon disponibilités. Pour garantir votre créneau, réservez en ligne 24/7." },
    { question: "Avez-vous du parking ?", answer: "Oui, parking gratuit et sécurisé sur place. Notre centre est également accessible aux personnes à mobilité réduite (accès PMR complet)." },
    { question: "Quels véhicules contrôlez-vous ?", answer: "Tous types : voitures, motos, scooters, utilitaires, camping-cars, véhicules de collection, 4x4, hybrides, électriques, GPL/GNV et voiturettes." },
  ];

  return (
    <SiteLayout>
      <SEOHead
        title={city.metaTitle}
        description={city.metaDescription}
        path={`/${city.slug}`}
      />
      <BreadcrumbSchema items={[
        { name: "Accueil", href: "/" },
        { name: `Contrôle technique ${city.name}`, href: `/${city.slug}` }
      ]} />
      <CityServiceAreaSchema cityName={city.name} />
      {city.testimonials.length > 0 && <CityReviewsSchema reviews={city.testimonials} />}
      <FAQSchema items={cityFAQ} />

      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${facadeCentre})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-body mb-6 border border-white/20">
              <MapPin className="w-4 h-4" />
              À {city.driveTime} de {city.name}
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              {city.heroTitle}
            </h1>
            <p className="font-body text-white/80 text-lg mb-8">
              {city.heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-autosur-red text-white font-heading font-semibold rounded-full hover:bg-autosur-red-light transition-all shadow-lg"
              >
                <img src={calendrierWhiteIcon} alt="" className="w-6 h-6" />
                Prendre RDV
              </Link>
              <a
                href="tel:+33149889240"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-heading font-semibold rounded-full hover:bg-white/20 transition-all border border-white/30"
              >
                <Phone className="w-5 h-5" />
                01 49 88 92 40
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Distance card */}
      <section className="py-6 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto -mt-12 relative z-20">
            {[
              { icon: Car, label: "En voiture", value: `${city.driveTime} (${city.distanceKm})` },
              { icon: Navigation, label: "Transports", value: city.transitTime },
              { icon: Clock, label: "Horaires", value: "Lun-Ven: 8h30-18h30" },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-xl p-5 shadow-lg border border-gray-100 text-center">
                <item.icon className="w-6 h-6 text-autosur-blue mx-auto mb-2" />
                <p className="font-heading font-bold text-autosur-navy text-sm">{item.label}</p>
                <p className="font-body text-muted-foreground text-sm">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro + Why choose us */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-6">
                Votre contrôle technique à {city.distanceKm} de {city.name}
              </h2>
              <div className="space-y-4 font-body text-muted-foreground">
                {city.intro.map((p, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>

              {/* City description */}
              {city.cityDescription && (
                <div className="mt-6 p-5 bg-autosur-navy/5 rounded-xl border border-autosur-navy/10">
                  <h3 className="font-heading font-bold text-autosur-navy text-sm mb-2">
                    📍 {city.name} en bref
                  </h3>
                  <p className="font-body text-muted-foreground text-sm">{city.cityDescription}</p>
                </div>
              )}

              {/* Price box */}
              <div className="mt-8 bg-autosur-blue/5 rounded-2xl p-6 border border-autosur-blue/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-autosur-navy text-lg">Contrôle technique VL</p>
                    <p className="font-body text-muted-foreground text-sm">Véhicule particulier</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-3xl font-bold text-autosur-red">73€</p>
                    <p className="font-body text-muted-foreground text-xs">
                      <span className="line-through">80€</span> À partir de (*)
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="mt-4 block w-full py-3 bg-autosur-red text-white font-heading font-semibold text-center rounded-full hover:bg-autosur-red-light transition-colors"
                >
                  Réserver en ligne
                </Link>
                <p className="font-body text-muted-foreground text-[10px] mt-2 text-center">(*) Offre non cumulable</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Why choose us */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-heading font-bold text-autosur-navy mb-4">
                  Pourquoi choisir notre centre à {city.name} ?
                </h3>
                <ul className="space-y-3">
                  {city.whyChoose.map((reason) => (
                    <li key={reason} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="font-body text-autosur-navy text-sm">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Image */}
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={facadeCentre}
                  alt={`Centre de contrôle technique Autosur près de ${city.name}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Directions détaillées */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Comment venir depuis {city.name}
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Notre centre est facilement accessible depuis {city.name}, que vous veniez en voiture ou en transports en commun.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="space-y-6">
              {/* Car directions */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-autosur-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Car className="w-6 h-6 text-autosur-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">En voiture – {city.driveTime}</h3>
                    {city.directionsDetailed ? (
                      <ol className="list-decimal list-inside space-y-1 font-body text-muted-foreground text-sm">
                        {city.directionsDetailed.car.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                    ) : (
                      <p className="font-body text-muted-foreground text-sm">{city.directionsText}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Transit */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-autosur-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Navigation className="w-6 h-6 text-autosur-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">En transports – {city.transitTime}</h3>
                    <p className="font-body text-muted-foreground text-sm">
                      {city.directionsDetailed ? city.directionsDetailed.transit : city.transitDetails}
                    </p>
                  </div>
                </div>
              </div>

              {/* Parking */}
              {city.directionsDetailed && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-autosur-navy mb-2">Parking gratuit</h3>
                      <p className="font-body text-muted-foreground text-sm">{city.directionsDetailed.parking}</p>
                    </div>
                  </div>
                </div>
              )}

              <a
                href={googleMapsDirections}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-autosur-blue text-white font-heading font-semibold rounded-full hover:bg-autosur-navy transition-colors"
              >
                <Navigation className="w-5 h-5" />
                Voir l'itinéraire sur Google Maps
              </a>

              {/* Adresse */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-autosur-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-autosur-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-1">Adresse</h3>
                    <a href="https://maps.app.goo.gl/QMbfid7TsPVr8ZB76" target="_blank" rel="noopener noreferrer" className="font-body text-muted-foreground hover:text-autosur-blue transition-colors text-sm block">
                      133, Boulevard Aristide Briand<br />93100 Montreuil-sous-Bois
                    </a>
                    <a href="tel:+33149889240" className="font-body text-autosur-blue hover:text-autosur-navy transition-colors text-sm mt-1 block">
                      01 49 88 92 40
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="space-y-6">
              <div className="aspect-square lg:aspect-auto rounded-2xl overflow-hidden shadow-lg bg-gray-200">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.215933153219!2d2.4535329999999997!3d48.873160000000006!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66d4a173a4aa9%3A0xd973599b099949f6!2sAUTOSUR%20MOTOSUR%20Contr%C3%B4le%20Technique%20AUTO%20%26%20MOTO!5e0!3m2!1sfr!2sfr!4v1770576435387!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: "400px" }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Centre Autosur Montreuil-sous-Bois - proche de ${city.name}`}
                />
              </div>
              {/* Centre photo */}
              <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-lg">
                <img src={accueilReception} alt={`Accueil du centre Autosur pour les clients de ${city.name}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Défauts courants */}
      {city.commonDefects && (
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4 text-center">
                Défauts courants détectés à {city.name}
              </h2>
              <p className="font-body text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                {city.commonDefects.context}
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {city.commonDefects.defects.map((defect) => (
                  <div key={defect.name} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        <h3 className="font-heading font-bold text-autosur-navy text-sm">{defect.name}</h3>
                      </div>
                      <span className="font-heading font-bold text-autosur-red text-lg">{defect.percent}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                      <div className="bg-autosur-red rounded-full h-2" style={{ width: `${defect.percent}%` }} />
                    </div>
                    <ul className="space-y-1">
                      {defect.details.map((detail, i) => (
                        <li key={i} className="font-body text-muted-foreground text-xs flex items-start gap-2">
                          <span className="text-autosur-red mt-1">•</span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="bg-autosur-blue/5 rounded-2xl p-6 border border-autosur-blue/20">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-autosur-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-1">Conseil pour les habitants de {city.name}</h3>
                    <p className="font-body text-muted-foreground text-sm">{city.commonDefects.advice}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Services + Tarifs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Services */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-6">
                Services disponibles à {city.name}
              </h2>
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
                <ul className="space-y-3">
                  {(city.services || [
                    "Contrôle technique voiture",
                    "Contrôle technique moto et scooter",
                    "Contrôle technique utilitaire",
                    "Contrôle technique collection",
                    "Contre-visite",
                    "Conseil en maintenance"
                  ]).map((service) => (
                    <li key={service} className="flex items-start gap-3">
                      <Wrench className="w-4 h-4 text-autosur-blue flex-shrink-0 mt-1" />
                      <span className="font-body text-autosur-navy text-sm">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {city.proTip && (
                <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                  <p className="font-heading font-bold text-autosur-navy text-sm mb-1">💡 Bon à savoir</p>
                  <p className="font-body text-muted-foreground text-sm">{city.proTip}</p>
                </div>
              )}
              {/* Centre photo */}
              <div className="mt-6 aspect-[16/9] rounded-2xl overflow-hidden shadow-lg">
                <img src={bancControle} alt={`Banc de contrôle technique pour les véhicules de ${city.name}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </motion.div>

            {/* Tarifs */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-6">
                Tarifs et horaires à {city.name}
              </h2>
              <div className="space-y-4">
                {[
                  { type: "Voiture", priceWeb: "73€", priceStd: "80€", duration: "30-45 min" },
                  { type: "Moto / Scooter", priceWeb: "70€", priceStd: "77€", duration: "20-30 min" },
                  { type: "Utilitaire", priceWeb: "79€", priceStd: "86€", duration: "30-60 min" },
                  { type: "Collection", priceWeb: "73€", priceStd: "80€", duration: "45-60 min" },
                ].map((tarif) => (
                  <div key={tarif.type} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                      <p className="font-heading font-bold text-autosur-navy text-sm">{tarif.type}</p>
                      <p className="font-body text-muted-foreground text-xs">{tarif.duration}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading font-bold text-autosur-red text-lg">{tarif.priceWeb}</p>
                      <p className="font-body text-muted-foreground text-xs line-through">{tarif.priceStd}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-heading font-bold text-autosur-navy mb-3">Horaires d'ouverture</h3>
                <div className="space-y-2 font-body text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Lundi – Vendredi</span><span className="font-semibold text-autosur-navy">8h30 – 18h30</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Samedi</span><span className="font-semibold text-autosur-navy">9h00 – 13h00</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Dimanche</span><span className="text-autosur-red font-semibold">Fermé</span></div>
                </div>
              </div>

              <Link
                to="/contact"
                className="mt-6 block w-full py-3 bg-autosur-red text-white font-heading font-semibold text-center rounded-full hover:bg-autosur-red-light transition-colors"
              >
                💰 Économiser 7€ en réservant en ligne
              </Link>
              <p className="font-body text-muted-foreground text-[10px] mt-2 text-center">(*) Tarifs web non cumulables avec d'autres offres</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <CityUseCases cityName={city.name} driveTime={city.driveTime} />

      {/* Comparison */}
      <CityComparison cityName={city.name} driveTime={city.driveTime} />

      {/* Defects Comparison by Region */}
      <CityDefectsComparison />

      {/* Testimonials */}
      {city.testimonials.length > 0 && (
        <TestimonialsCarousel
          testimonials={city.testimonials}
          title={`Avis clients de ${city.name}`}
          subtitle={`Ce que disent nos clients de ${city.name} • 343 avis Google 4.9/5`}
        />
      )}

      {/* Internal links enrichis */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-8 text-center">
            Découvrez aussi
          </h2>
          
          {/* Vehicle types */}
          <div className="max-w-4xl mx-auto mb-10">
            <h3 className="font-heading font-semibold text-autosur-navy mb-4 text-sm uppercase tracking-wide">Nos contrôles techniques</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/vehicules/voitures" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all text-center">
                <h4 className="font-heading font-bold text-autosur-navy text-sm mb-1">CT Voiture</h4>
                <p className="font-body text-muted-foreground text-xs">Berlines, SUV, citadines – dès 73€</p>
              </Link>
              <Link to="/vehicules/motos" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all text-center">
                <h4 className="font-heading font-bold text-autosur-navy text-sm mb-1">CT Moto</h4>
                <p className="font-body text-muted-foreground text-xs">Obligatoire depuis 2024 – dès 70€</p>
              </Link>
              <Link to="/vehicules/utilitaires" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all text-center">
                <h4 className="font-heading font-bold text-autosur-navy text-sm mb-1">CT Utilitaire</h4>
                <p className="font-body text-muted-foreground text-xs">Fourgons, camping-cars – dès 79€</p>
              </Link>
              <Link to="/vehicules/collection" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all text-center">
                <h4 className="font-heading font-bold text-autosur-navy text-sm mb-1">CT Collection</h4>
                <p className="font-body text-muted-foreground text-xs">Véhicules anciens – dès 73€</p>
              </Link>
            </div>
          </div>

          {/* Blog articles */}
          <div className="max-w-4xl mx-auto mb-10">
            <h3 className="font-heading font-semibold text-autosur-navy mb-4 text-sm uppercase tracking-wide">Articles utiles</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <Link to="/blog/premier-controle-technique-4-ans-guide" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all">
                <h4 className="font-heading font-bold text-autosur-navy text-sm mb-1">Premier CT à 4 ans</h4>
                <p className="font-body text-muted-foreground text-xs">Guide complet pour votre 1er contrôle</p>
              </Link>
              <Link to="/blog/controle-technique-moto-2026-echeances-obligations" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all">
                <h4 className="font-heading font-bold text-autosur-navy text-sm mb-1">CT Moto 2026</h4>
                <p className="font-body text-muted-foreground text-xs">Échéances et obligations légales</p>
              </Link>
              <Link to="/blog/economiser-controle-technique-93-astuces" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all">
                <h4 className="font-heading font-bold text-autosur-navy text-sm mb-1">Économiser sur son CT</h4>
                <p className="font-body text-muted-foreground text-xs">5 astuces pour réduire vos frais</p>
              </Link>
            </div>
          </div>

          {/* Other pages */}
          <div className="max-w-4xl mx-auto">
            <div className="grid sm:grid-cols-3 gap-4">
              <Link to="/prestations" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all text-center">
                <h4 className="font-heading font-bold text-autosur-navy text-sm mb-1">Nos prestations</h4>
                <p className="font-body text-muted-foreground text-xs">Tarifs et services détaillés</p>
              </Link>
              <Link to="/le-centre" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all text-center">
                <h4 className="font-heading font-bold text-autosur-navy text-sm mb-1">Notre centre</h4>
                <p className="font-body text-muted-foreground text-xs">Visite virtuelle et équipe</p>
              </Link>
              <Link to="/faq" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all text-center">
                <h4 className="font-heading font-bold text-autosur-navy text-sm mb-1">FAQ</h4>
                <p className="font-body text-muted-foreground text-xs">Toutes vos questions</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby cities */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-6 text-center">
            Villes proches de {city.name}
          </h2>
          <div className="flex flex-wrap gap-3 justify-center">
            {city.nearbyAreas.map((area) => {
              const linkedCity = cities.find(c => c.name === area);
              if (!linkedCity) return null;
              return (
                <Link
                  key={area}
                  to={`/${linkedCity.slug}`}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-full font-body text-autosur-navy hover:border-autosur-blue/50 hover:bg-autosur-blue/5 transition-all text-sm"
                >
                  <MapPin className="w-4 h-4 text-autosur-blue" />
                  CT {area} – {linkedCity.driveTime}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-8 text-center">
              Questions fréquentes – {city.name}
            </h2>
            <div className="space-y-4">
              {cityFAQ.map((faq, index) => (
                <details key={index} className="bg-white rounded-xl border border-gray-100 shadow-sm group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-heading font-semibold text-autosur-navy text-sm hover:text-autosur-blue transition-colors">
                    {faq.question}
                    <ChevronDown className="w-5 h-5 text-muted-foreground group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
                  </summary>
                  <div className="px-5 pb-5 pt-0">
                    <p className="font-body text-muted-foreground text-sm">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <CTASection 
        title={`Habitant de ${city.name} ?`}
        subtitle="Réservez votre contrôle technique en ligne et profitez de nos tarifs préférentiels."
      />
    </SiteLayout>
  );
};

export default CityLandingPage;
